import { useEffect } from 'react';

import { useForm } from 'react-hook-form';

import {
  Text
} from '@comicrelief/component-library';

import { SubmitButton } from '../../components/global/Global.style';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { clearErrorMessage } from '@/features/paymentProcess/transactionSlice';
import PaymentErrorText from '@/features/paymentProcess/PaymentErrorText';

import { setActivities, setActivityOther, Activity } from './payinSlice';
import { activityFields, activityOptions, getFormResolver } from './payinActivityFields';
import StepBack from '@/components/stepBack/StepBack';
import { getCurrencySymbol } from '@/locale';
import { getPageHeaderInitialText, getPageHeaderSupplementaryText } from '@/utils/paymentTextUtils';
import {
  PayInActivityCheckbox, PayInActivityCheckboxLabel, PayInActivityInformationalTextContainer, PayInActivityOtherInput, PayInActivityWrapper
} from './PayInActivity.style';
import PageHeader from '@/components/pageHeader/PageHeader';

function PayinActivity() {
  const dispatch = useAppDispatch();
  const payinState = useAppSelector(state => state.payin);

  const journeyNavigate = useJourneyNavigate();

  const formMethods = useForm({
    mode: 'onSubmit',
    resolver: getFormResolver(),
    defaultValues: {
      activities: payinState.activities,
      activityOther: payinState.activityOther
    }
  });

  const {
    register,
    watch,
    handleSubmit,
    getValues,
    formState: {
      errors: formErrors,
    },
  } = formMethods;

  const formState = getValues();

  useEffect(() => {
    const watchInputs = watch((formValues, { name }) => {
      if (!name) {
        return;
      }

      // Handle activities checkboxes
      if (name === 'activities') {
        const newActivities = formValues[name] as Activity[];
        dispatch(setActivities(newActivities));
      } else if (name === 'activityOther') {
        dispatch(setActivityOther(formValues[name] as string));
      }
    });
    return () => watchInputs.unsubscribe();
  }, [watch, dispatch]);

  const onSubmit = () => {
    dispatch(clearErrorMessage());
    journeyNavigate('next');
  };

  const getImageFilePath = (activity: Activity) => {
    const activityMap: Record<string, string> = {
      anything_a_thon: 'Activity_Anything-a-thon.png',
      cycling: 'Activity_Cycling.png',
      dance_a_thon: 'Activity_Dance-a-thon.png',
      endurance_distance_challenge: 'Activity_Endurance.png',
      fancy_dress: 'Activity_FancyDress.png',
      bake_sale: 'Activity_Baking.png',
      baking: 'Activity_Baking.png',
      get_your_kit_on_dress_up_day: 'Activity_FancyDress.png',
      office_olympics: 'Activity_Olympics.png',
      quiz: 'Activity_Quiz.png',
      quiz_other_games: 'Activity_Quiz.png',
      quiz_games: 'Activity_Quiz.png',
      running: 'Activity_Running.png',
      rowing: 'Activity_Rowing.png',
      swimming: 'Activity_Swimming.png',
      selling_wristbands: 'Activity_Wristbands.png',
      sponsored_serenity: 'Activity_Meditation.png',
      sport_relief_mile: 'Activity_SRmile.png',
      sweepstake_poster_game: 'Activity_Sweepstake.png',
      team_games_sport: 'Activity_TeamSport.png',
      sport_activity: 'Activity_TeamSport.png',
      tea_break_challenge_game: 'Activity_TeaBreak.png',
      team_challenge: 'Activity_TeamChallenge.png',
      sponsored_challenge: 'Activity_TeamChallenge.png',
      tournament: 'Activity_TeacherPupil.png',
      varsity_throw_down: 'Activity_Varsity.png',
      walking_hiking: 'Activity_Walking.png',
      talent_show: 'Activity_Dance-a-thon.png',
      other: 'Activity_Something_Else.png'
    };

    return activityMap[activity] || 'InfamousChest.png';
  };

  const errorMessage = formErrors?.activities?.message ?? formErrors?.activityOther?.message;

  return (
    <>
      <form onSubmit={event => void handleSubmit(onSubmit)(event)}>

        <PageHeader
          currentStep={2}
          totalSteps={5}
          headerText="How did you take part?"
          initialText={getPageHeaderInitialText({ isPayIn: true })}
          supplementaryText={getPageHeaderSupplementaryText({ isPayIn: true, payinState })}
          currencySymbol={getCurrencySymbol()}
        />

        <PayInActivityWrapper>
          {!!payinState.audience && Object.entries(activityOptions[payinState.audience]).map(([activityValue, activityData]) => (
            <div key={activityValue}>
              <PayInActivityCheckboxLabel
                $isChecked={Boolean(formState?.activities?.includes(activityValue as Activity))}
                $errorState={!!formErrors?.activities?.message}
                data-test={activityData.testId}
              >
                <PayInActivityCheckbox
                  value={activityValue}
                  checked={Boolean(formState?.activities?.includes(activityValue as Activity))}
                  {...register(activityFields.activities.id)}
                />
                <img src={`/assets/images/payin/${getImageFilePath(activityValue as Activity)}`} alt={`Activity ${activityValue}`} />
                <p>{activityData.label}</p>
              </PayInActivityCheckboxLabel>
            </div>
          ))}
        </PayInActivityWrapper>

        {(payinState.activities?.includes('other')) ? (
          <div>
            <PayInActivityOtherInput
              id="activity-other-input"
              label={activityFields.activityOther.label}
              type={activityFields.activityOther.type}
              hideError
              {...register(activityFields.activityOther.id)}
            />
          </div>
        ) : null}

        <PayInActivityInformationalTextContainer $errorState={!!errorMessage}>
          {errorMessage
            ? <Text color="red" weight="bold">{errorMessage}</Text>
            : <Text size="xs" color="black">Select at least one activity</Text>}
        </PayInActivityInformationalTextContainer>

        <PaymentErrorText />

        <SubmitButton
          fullWidth
          data-test="payinActivityContinue"
        >
          Continue to your details
        </SubmitButton>

      </form>

      <StepBack data-test="payinActivityGoBack" />

    </>
  );
}

export default PayinActivity;
